import React, { useEffect, useImperativeHandle, forwardRef, ReactNode, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Fade,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AutoSuggest, Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import { useTranslation } from "react-i18next";
import { colors } from "@zapalacorp/tokens";
import { MOVING_COMPANY_AGENT_SIGN_IN_SELECT } from "Constants";
import { useAgentSelectCompanyActions } from "./useAgentSelectCompanyActions";
import { Company } from "Models/company";
import { useMutation } from "@tanstack/react-query";
import AgentService from "Services/agent.service";
import {
  AcceptAndAssociateShipeeziNetworkInvitePayload,
  AcceptIndependentShipeeziNetworkInvitePayload,
  ValidateShipeeziNetworkInviteTokenPayload,
} from "../../../../../pages/public/AgentInvite/AcceptShipeeziNetworkInvite";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { ArrowDown2, SearchNormal, SearchNormal1 } from "iconsax-react";

export const AgentSelectCompanyModal = () => {
  const { t: translate } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [companySelected, setCompanySelected] = useState<Company>();
  const [companyWhoInvitedName, setCompanyWhoInvitedName] = useState<string>();
  const [skipCompanyChoice, setSkipCompanyChoice] = useState<boolean>();
  const [token, setToken] = useState<string | null>();

  const { getMovingCompaniesWithAccess } = useAgentSelectCompanyActions();

  useEffect(() => {
    const token = localStorage.getItem(MOVING_COMPANY_AGENT_SIGN_IN_SELECT);
    setToken(token);
  }, []);

  useEffect(() => {
    if (token && !open && !validateMutation.isLoading) {
      validateMutation.mutate({ token });
    }
  }, [token]);

  useEffect(() => {
    if (skipCompanyChoice && token) {
      acceptIndependentMutation.mutate({ token });
    }
  }, [skipCompanyChoice]);

  const validateMutation = useMutation({
    mutationFn: (payload: ValidateShipeeziNetworkInviteTokenPayload) =>
      AgentService.validateInvite(payload),
    onSuccess: ({ data }) => {
      console.log("data received from validateInvite: ", data);
      if (data.createdCompany) {
        setOpen(true);
        setCompanyWhoInvitedName(data.companyName);
      } else {
        setSkipCompanyChoice(true);
      }
    },
    onError: (error) => {
      console.error("Erro ao validar convite:", error);
    },
  });

  const selectInviteMutation = useMutation({
    mutationFn: (payload: AcceptAndAssociateShipeeziNetworkInvitePayload) =>
      AgentService.acceptInviteAndAssociate(payload),
    onSuccess: ({ data }) => {
      NotificationToast.success(
        "Connection with [Company_name] is successful".replace(
          "[Company_name]",
          companyWhoInvitedName!
        )
      );
      handleClean();
    },
    onError: (error) => {
      NotificationToast.success(
        translate("anErrorOccurredWhileTryingToConnectTheCompaniesPleaseTryAgainLater")
      );
    },
  });

  const acceptIndependentMutation = useMutation({
    mutationFn: (payload: AcceptIndependentShipeeziNetworkInvitePayload) =>
      AgentService.acceptInvite(payload),
    onSuccess: () => {
      handleClean();
    },
    onError: (error) => {
      console.error("Erro ao aceitar convite independente:", error);
    },
  });

  const handleClean = (): void => {
    localStorage.removeItem(MOVING_COMPANY_AGENT_SIGN_IN_SELECT);
    setOpen(false);
  };

  const handleClose = (_event: {}, reason?: string): void => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    handleClean();
  };

  const handleCloseButton = (): void => {
    handleClean();
  };

  const handleConfirm = (): void => {
    if (!token || !companySelected?.id) return;

    selectInviteMutation.mutate({ selectedCompanyId: companySelected.id, token });
  };

  const { data: movingCompanies, isLoading: isLoadingMovingCompanies } =
    getMovingCompaniesWithAccess();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        },
      }}
      PaperProps={{
        sx: {
          maxWidth: "448px",
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        id="confirmation-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
          pb: 0,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 600, color: colors.gray600, fontSize: "22px", lineHeight: "28px" }}
        >
          {translate("selectCompany")}
        </Typography>
        <IconButton
          aria-label="Close modal"
          onClick={handleCloseButton}
          sx={{
            color: colors.gray600,
            "&:hover": {
              backgroundColor: colors.gray100,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: "8px !important" }}>
        <Typography
          id="confirmation-dialog-description"
          variant="body1"
          sx={{
            lineHeight: 1.6,
            color: colors.gray600,
            fontSize: "16px",
            letterSpacing: "0.5px",
          }}
        >
          {"Select a company you want to connect with [Company_name]’s Shipeezi network".replace(
            "[Company_name]",
            companyWhoInvitedName ?? ""
          )}
        </Typography>
      </DialogContent>

      <DialogContent>
        <AutoSuggest
          value={companySelected}
          onChange={(value) => {
            setCompanySelected(value);
          }}
          getOptionSelected={(option, value) => option?.id === value?.id}
          getOptionKey={(company) => `${company.id}`}
          getOptionLabel={(option) => option.name}
          options={movingCompanies}
          startIcon={<SearchNormal1 size={20} />}
          selectIcon={<ArrowDown2 size={20} />}
        />
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.gray200}`,
          gap: "16px",
          pt: "16px",
          pb: "16px",
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          customColor={Color.SUCCESS}
          onClick={handleConfirm}
          loading={
            isLoadingMovingCompanies || validateMutation.isLoading || selectInviteMutation.isLoading
          }
          disabled={
            isLoadingMovingCompanies ||
            !companySelected ||
            validateMutation.isLoading ||
            selectInviteMutation.isLoading
          }
        >
          {translate("connect")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AgentSelectCompanyModal.displayName = "AgentSelectCompanyModal";
